import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { API_URL } from '../../helpers/globals';
import { map, share } from 'rxjs/operators';

export interface LastActivityDay {
    // drivingCurrentDay: number;
    crew: boolean;
    // dayStart: number;
    // dayEnd: number;
    // dayStartHR: string;
    // dayEndHR: string;
}

export interface LastActivity {
    activity: string;
    activityLength: number;
    startTime: number;
    startTimeHR: string;
}

export interface PreviousActivity {
    activity: string;
    activityLength: number;
    startTime: number;
}

export interface RestLastDaily {
    restStart: number;
    restEnd: number;
    restLength: number;
}

export interface RestLastWeekly1 {
    start: number;
    end: number;
    length: number;
}

export interface RestLastWeekly2 {
    start: number;
    end: number;
    length: number;
}

export interface RestLastWeekly3 {
    start: number;
    end: number;
    length: number;
}

export interface Projections {
    nextWorkStart: number;
    nextWorkLength: number;
    nextWorkComments: any[];
}

export interface WorkTimes {
    maxWorkWhenNightWorkPerformed: number;
    nightWorkPerformedThisDay: boolean;
    totalWorkToday: number;
    workContinuousWithoutBreak: number;
    workContinuousWithoutBreakMax: string;
    workTimeAverageLast16Weeks: number;
    workTimeWeekly: number;
    workTimeWeeklyMax: number;
    justWorkForToday: number;
    workInformationUpdateTime: number;
}

export interface CalculatedValues {
    baseTime: number;
    baseTimeHR: string;
    projections: Projections;
    workTimes: WorkTimes;
    drivingContinuousRemaining: number;
    drivingContinuousMax: number;
    drivingDailyRemaining: number;
    drivingDailyRemainingCurrentDay: number;
    drivingDailyMAX: string;
    /**
     * True when the daily driving time cannot be calculated from the card data, so the Daily and Weekly
     * driving values must be shown as "--:--".
     */
    drivingDailyTimeNotAvailable: boolean;
    drivingWeeklyRemaining: number;
    drivingWeeklyMax: number;
    drivingTwoWeeklyRemaining: number;
    drivingTwoWeeklyMax: number;
    nextDailyBreakStart: number;
    nextDailyBreakStartHR: string;
    nextDailyBreakStartAsCrew: number;
    nextDailyBreakStartAsCrewHR: string;
    nextWeeklyBreakStart: number;
    nextWeeklyBreakStartHR: string;
    nextWeeklyBreakLength?: number | null; // minutes
    nextBreak?: number | null; // minutes until the next break
    nextBreakField?: string | null; // the limit nextBreak comes from, e.g. 'drivingContinuousRemaining'
    moreThan9hDrivingTimesFromLastWeeklyBreak: number;
    moreThan9hDrivingTimesFromLastWeeklyBreakMax: number;
    lessThan11hBreakTimesFromLastWeeklyBreak: number;
    lessThan11hBreakTimesFromLastWeeklyBreakMax: number;
}

export interface UploadInformation {
    uploadTimeStamp: number;
    uploadTimeStampHR: string;
    minutesSinceUpload: number;
    minutesBetweenLastActivityAndUpload: number;
    minutesSinceLastActivity: number;
}

export interface DriverInfo {
    cardNumber: string;
    driverId: number | null;
    name: string;
    cardInserted: boolean;
}

export type VehiclePositionAndDrivers = {
    lat: number;
    lng: number;
    vin: string;
    registrationNumber: string;
    terminalMac?: string;
    /** TA - tachograph available */
    ta?: boolean;
    /** CI - card inserted */
    ci?: boolean;
    /** CD - card download */
    cd?: boolean;
    drivers: string[];
    address?: string;
    lastPositionTime?: string;
    speed?: number;
    outdated?: boolean;
}

export interface Result {
    lastActivityDay: LastActivityDay;
    lastActivity: LastActivity;
    // previousActivity: PreviousActivity;
    drivingWeek: number;
    restLastDaily: RestLastDaily;
    drivingLast2Weeks: number;
    restLastWeekly1: RestLastWeekly1;
    // restLastWeekly2: RestLastWeekly2;
    // restLastWeekly3: RestLastWeekly3;
    calculatedValues: CalculatedValues;
    uploadInformation: UploadInformation;
    // message: string;
    driverInfo: DriverInfo;

    vehicleIdentificationNumber: string;
    terminalMac?: string;
    slot: number;
}

export interface DriverTraceResponseOld {
    result: Result[];
    // vehiclePositionAndDrivers: VehiclePositionAndDrivers[];
}
export interface DriverTraceResponse {
    result: Result[];
    vehiclePositionAndDrivers: VehiclePositionAndDrivers[];
}

export interface VehicleV2BasicLastPosition {
    id: number;
    deviceId: number;
    protocol: string;
    serverTime: string;
    deviceTime: string;
    fixTime: string;
    outdated: boolean;
    valid: boolean;
    latitude: number;
    longitude: number;
    altitude: number;
    speed: number;
    course: number;
    address: string | null;
    accuracy: number;
    network: any;
    geofenceIds: any[];
    attributes: {
        ignition?: boolean;
        motion?: boolean;
        odometer?: number;
        batteryLevel?: number;
        sat?: number;
        [key: string]: any;
    };
}

export interface VehicleV2BasicRecord {
    id: number;
    identificationNumber: string;
    registrationNumber: string;
    rduId: string;
    lastPosition: VehicleV2BasicLastPosition;
}

export interface VehiclesListV2BasicResponse {
    Records: VehicleV2BasicRecord[];
    TotalRecordCount: number;
}

export type RefreshDataWithoutRDUAdminResponseError = {
    message: string;
    error: string;
}

export type RefreshDataWithoutRDUAdminResponse = {
    message: string;
    response: 'error' | 'success';
    notifiedClients: number;
}

@Injectable({
    providedIn: 'root'
})
export class DrivertraceService {

    constructor(private http: HttpClient) {
    }

    getDriversTrace(companyId: number, timezone: string): Observable<DriverTraceResponse> {
        const params: HttpParams = new HttpParams()
            .append('clientId', '' + companyId)
            .append('timeZone', timezone);
        return this.http.get<DriverTraceResponse>(API_URL + 'debug/traceCompany',
            { params: params, withCredentials: true })
            .pipe(share(), map(response => {
                return response;
            }));
    }

    refreshDataWithoutRDUAdmin(companyId: number, cardNumber: string) {
        return this.http.get<RefreshDataWithoutRDUAdminResponse | RefreshDataWithoutRDUAdminResponseError>(API_URL + `debug/refreshDataWithoutRDUAdmin/${companyId}/${cardNumber}`, { withCredentials: true })
            .pipe(share(), map(response => {
                return response;
            }));
    }

    getVehiclesListV2Basic(clientId: number): Observable<VehiclesListV2BasicResponse> {
        const params: HttpParams = new HttpParams()
            .append('clientId', '' + clientId);
        return this.http.get<VehiclesListV2BasicResponse>(API_URL + 'vehicle/getVehiclesListV2Basic',
            { params: params, withCredentials: true })
            .pipe(share());
    }

    getDriversTrace2(companyId: number, timezone: string): Observable<DriverTraceResponse> {
        return this.http.get<TachographResponse>(`${API_URL}rdu/readTachographParameters/${companyId}/1/50`,
            { withCredentials: true })
            .pipe(share(), map(response => {
                const str = (value: string | null | undefined) => value ?? '';
                const num = (value: number | null | undefined) => value ?? 0;
                const bool = (value: boolean | null | undefined) => !!value;
                const mapDriver = (item: DeepNullable<TachographParameter>, driver: DeepNullable<Driver>, slot: 1 | 2): Result => ({
                    vehicleIdentificationNumber: str(item.vehicleIdentificationNumber),
                    terminalMac: str(item.terminalMac),
                    slot,
                    driverInfo: {
                        cardNumber: str(driver.identification),
                        driverId: driver.driverId ?? null,
                        name: str(driver.driverName),
                        cardInserted: bool(driver.cardInserted),
                    },
                    lastActivity: {
                        activity: str(driver.lastActivity?.activity),
                        activityLength: num(driver.lastActivity?.durationOfLastActivity),
                        startTime: num(driver.baseTime) - (num(driver.lastActivity?.durationOfLastActivity) * 60),
                        startTimeHR: str(driver.baseTimeHR),
                    },
                    drivingWeek: num(driver.drivingWeeklyTime),
                    restLastDaily: {
                        restStart: num(driver.lastDailyRest?.start),
                        restEnd: num(driver.lastDailyRest?.end),
                        restLength: num(driver.lastDailyRest?.start) && num(driver.lastDailyRest?.end)
                            ? Math.round((num(driver.lastDailyRest?.end) - num(driver.lastDailyRest?.start)) / 60)
                            : 0,
                    },
                    lastActivityDay: {
                        crew: bool(driver.lastActivityDay?.crew),
                    },
                    restLastWeekly1: {
                        start: num(driver.lastWeeklyRest?.start),
                        end: num(driver.lastWeeklyRest?.end),
                        length: num(driver.lastWeeklyRest?.start) && num(driver.lastWeeklyRest?.end)
                            ? Math.round((num(driver.lastWeeklyRest?.end) - num(driver.lastWeeklyRest?.start)) / 60)
                            : 0,
                    },
                    drivingLast2Weeks: num(driver.drivingLast2Weeks),
                    calculatedValues: {
                        workTimes: {
                            workTimeWeekly: num(driver.workTimes?.workTimeWeekly),
                            workTimeWeeklyMax: num(driver.workTimes?.workTimeWeeklyMax),
                            workTimeAverageLast16Weeks: num(driver.workTimes?.workTimeAverageLast16Weeks),
                            nightWorkPerformedThisDay: bool(driver.workTimes?.nightWorkPerformedThisDay),
                            maxWorkWhenNightWorkPerformed: num(driver.workTimes?.maxWorkWhenNightWorkPerformed),
                            workContinuousWithoutBreak: num(driver.workTimes?.workContinuousWithoutBreak),
                            workContinuousWithoutBreakMax: str(driver.workTimes?.workContinuousWithoutBreakMax),
                            totalWorkToday: num(driver.workTimes?.totalWorkToday),
                            justWorkForToday: num(driver.workTimes?.justWorkForToday),
                            workInformationUpdateTime: num(driver.workTimes?.workInformationUpdateTime),
                        },
                        baseTime: num(driver.baseTime),
                        baseTimeHR: str(driver.baseTimeHR),
                        drivingTwoWeeklyRemaining: num(driver.drivingTwoWeeklyRemaining),
                        drivingTwoWeeklyMax: num(driver.drivingTwoWeeklyMax),
                        projections: {
                            nextWorkStart: num(driver.projections?.nextWorkStart),
                            nextWorkLength: num(driver.projections?.nextWorkLength),
                            nextWorkComments: [],
                        },
                        drivingContinuousRemaining: num(driver.drivingContinuousRemaining),
                        drivingContinuousMax: num(driver.drivingContinuousMax),
                        drivingDailyRemaining: num(driver.drivingDailyRemaining),
                        drivingDailyRemainingCurrentDay: num(driver.drivingDailyRemaining),
                        drivingDailyMAX: num(driver.drivingDailyMax) + '',
                        drivingDailyTimeNotAvailable: bool(driver.drivingDailyTimeNotAvailable),
                        drivingWeeklyRemaining: num(driver.drivingWeeklyRemaining),
                        drivingWeeklyMax: num(driver.drivingWeeklyMax),
                        nextDailyBreakStart: num(driver.nextDailyBreakStart),
                        nextDailyBreakStartHR: str(driver.nextDailyBreakStartHR),
                        nextDailyBreakStartAsCrew: 0,
                        nextDailyBreakStartAsCrewHR: '',
                        nextWeeklyBreakStart: num(driver.nextWeeklyBreakStart),
                        nextWeeklyBreakStartHR: str(driver.nextWeeklyBreakStartHR),
                        nextWeeklyBreakLength: driver.nextWeeklyBreakLength ?? null,
                        nextBreak: driver.nextBreak ?? null,
                        nextBreakField: driver.nextBreakField ?? null,
                        moreThan9hDrivingTimesFromLastWeeklyBreak: num(driver.moreThan9hDrivingTimesFromLastWeeklyBreak),
                        moreThan9hDrivingTimesFromLastWeeklyBreakMax: num(driver.moreThan9hDrivingTimesFromLastWeeklyBreakMax),
                        lessThan11hBreakTimesFromLastWeeklyBreak: num(driver.lessThan11hBreakTimesFromLastWeeklyBreak),
                        lessThan11hBreakTimesFromLastWeeklyBreakMax: num(driver.lessThan11hBreakTimesFromLastWeeklyBreakMax),
                    },
                    uploadInformation: {
                        uploadTimeStamp: num(driver.uploadInformation?.uploadTimeStamp),
                        uploadTimeStampHR: str(driver.uploadInformation?.uploadTimeStampHR),
                        minutesSinceUpload: num(driver.uploadInformation?.uploadTimeStamp),
                        minutesBetweenLastActivityAndUpload: 0,
                        minutesSinceLastActivity: 0,
                    },
                });

                const data: Result[] = [];
                const tachographParameters = response.tachographParameters ?? [];
                for (const item of tachographParameters) {
                    if (!item) {
                        continue;
                    }
                    if (item.driver1 && str(item.driver1.identification).length > 0) {
                        data.push(mapDriver(item, item.driver1, 1));
                    }
                    if (item.driver2 && str(item.driver2.identification).length > 0) {
                        data.push(mapDriver(item, item.driver2, 2));
                    }
                }
                return {
                    result: data,
                    vehiclePositionAndDrivers: tachographParameters
                        .filter((item): item is DeepNullable<TachographParameter> => item !== null)
                        .map(item => ({
                            // lat: item.vehiclePosition?.vehiclePositionLatitude??0.0,
                            // lng: item.vehiclePosition?.vehiclePositionLongitude??0.0,
                            lat: 0,
                            lng: 0,
                            vin: str(item.vehicleIdentificationNumber),
                            terminalMac: str(item.terminalMac),
                            ta: item.ta ?? undefined,
                            ci: item.ci ?? undefined,
                            cd: item.cd ?? undefined,
                            registrationNumber: str(item.vehicleRegistrationNumber),
                            drivers: [
                                item.driver1 ? str(item.driver1.driverName) : '',
                                item.driver2 ? str(item.driver2.driverName) : ''
                            ].filter(name => name !== '')
                        }))
                }
            }));
    }
}

const dummyResponse: TachographResponse = {
    "tachographParameters": [
        {
            "terminalId": 41505,
            "terminalName": "TE860621060690981",
            "terminalMac": "TE860621060690981",
            "terminalStatus": "TACHOGRAPH_NOT_AVAILABLE_WITH_CARD_DOWNLOAD",
            "driver1": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 0,
                "drivingLast2WeeksHR": "0h:00m",
                "drivingTwoWeeklyRemaining": 5400,
                "drivingTwoWeeklyRemainingHR": "90h:00m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "NOT_AVAILABLE",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": 1779712731,
                    "uploadTimeStampHR": "2026.05.25 12:38:51"
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 0,
                    "workTimeWeeklyMaxHR": "0h:00m",
                    "workTimeAverageLast16Weeks": 0,
                    "workTimeAverageLast16WeeksHR": "0h:00m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 0,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 0,
                    "workInformationUpdateTimeHR": "1970.01.01 00:00:00"
                },
                "projections": {
                    "nextWorkStart": 0,
                    "nextWorkStartHR": "1970.01.01 00:00:00",
                    "nextWorkLength": 0
                },
                "driverName": "Attila Menyhart",
                "identification": "0000000005QAA0",
                "driverId": 119551,
                "nextWeeklyBreakStart": 0,
                "nextWeeklyBreakStartHR": "",
                "nextDailyBreakStart": 0,
                "nextDailyBreakStartHR": "",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 1779712731,
                "baseTimeHR": "2026.05.25 12:38:51"
            },
            "driver2": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 0,
                "drivingLast2WeeksHR": "0h:00m",
                "drivingTwoWeeklyRemaining": 5400,
                "drivingTwoWeeklyRemainingHR": "90h:00m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "NOT_AVAILABLE",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": null,
                    "uploadTimeStampHR": ""
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 0,
                    "workTimeWeeklyMaxHR": "0h:00m",
                    "workTimeAverageLast16Weeks": 0,
                    "workTimeAverageLast16WeeksHR": "0h:00m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 0,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 0,
                    "workInformationUpdateTimeHR": "1970.01.01 00:00:00"
                },
                "projections": {
                    "nextWorkStart": 0,
                    "nextWorkStartHR": "1970.01.01 00:00:00",
                    "nextWorkLength": 0
                },
                "driverName": "",
                "identification": "",
                "driverId": 0,
                "nextWeeklyBreakStart": 0,
                "nextWeeklyBreakStartHR": "",
                "nextDailyBreakStart": 0,
                "nextDailyBreakStartHR": "",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 0,
                "baseTimeHR": ""
            },
            "vehicleRegistrationNumber": "MS-04-KRI",
            "vehicleIdentificationNumber": "YV2RT40A5KB917107",
            "outOfScopeMode": null,
            "vehicleSpeed": null,
            "updatedAt": null,
            "uploadInformation": null
        },
        {
            "terminalId": 45146,
            "terminalName": "TP860621066646649",
            "terminalMac": "TP860621066646649",
            "terminalStatus": "TACHOGRAPH_NOT_AVAILABLE_WITH_CARD_DOWNLOAD",
            "driver1": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 0,
                "drivingLast2WeeksHR": "0h:00m",
                "drivingTwoWeeklyRemaining": 5400,
                "drivingTwoWeeklyRemainingHR": "90h:00m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "NOT_AVAILABLE",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": 1779761695,
                    "uploadTimeStampHR": "2026.05.26 02:14:55"
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 0,
                    "workTimeWeeklyMaxHR": "0h:00m",
                    "workTimeAverageLast16Weeks": 0,
                    "workTimeAverageLast16WeeksHR": "0h:00m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 0,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 0,
                    "workInformationUpdateTimeHR": "1970.01.01 00:00:00"
                },
                "projections": {
                    "nextWorkStart": 0,
                    "nextWorkStartHR": "1970.01.01 00:00:00",
                    "nextWorkLength": 0
                },
                "driverName": "Attila Kacso",
                "identification": "0000000008R6A0",
                "driverId": 152243,
                "nextWeeklyBreakStart": 0,
                "nextWeeklyBreakStartHR": "",
                "nextDailyBreakStart": 0,
                "nextDailyBreakStartHR": "",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 1779761695,
                "baseTimeHR": "2026.05.26 02:14:55"
            },
            "driver2": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 0,
                "drivingLast2WeeksHR": "0h:00m",
                "drivingTwoWeeklyRemaining": 5400,
                "drivingTwoWeeklyRemainingHR": "90h:00m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "NOT_AVAILABLE",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": null,
                    "uploadTimeStampHR": ""
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 0,
                    "workTimeWeeklyMaxHR": "0h:00m",
                    "workTimeAverageLast16Weeks": 0,
                    "workTimeAverageLast16WeeksHR": "0h:00m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 0,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 0,
                    "workInformationUpdateTimeHR": "1970.01.01 00:00:00"
                },
                "projections": {
                    "nextWorkStart": 0,
                    "nextWorkStartHR": "1970.01.01 00:00:00",
                    "nextWorkLength": 0
                },
                "driverName": "",
                "identification": "",
                "driverId": 0,
                "nextWeeklyBreakStart": 0,
                "nextWeeklyBreakStartHR": "",
                "nextDailyBreakStart": 0,
                "nextDailyBreakStartHR": "",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 0,
                "baseTimeHR": ""
            },
            "vehicleRegistrationNumber": "B 255 KRI",
            "vehicleIdentificationNumber": "YV2RT40A3GB782782",
            "outOfScopeMode": null,
            "vehicleSpeed": null,
            "updatedAt": null,
            "uploadInformation": null
        },
        {
            "terminalId": 45147,
            "terminalName": "TP860621066645088",
            "terminalMac": "TP860621066645088",
            "terminalStatus": "TACHOGRAPH_AVAILABLE_CARD_INSERTED_WITHOUT_CARD_DOWNLOAD",
            "driver1": null,
            "driver2": null,
            "vehicleRegistrationNumber": "MS-68-KRI",
            "vehicleIdentificationNumber": "YV2RT40A6PB435513",
            "outOfScopeMode": null,
            "vehicleSpeed": null,
            "updatedAt": null,
            "uploadInformation": null
        },
        {
            "terminalId": 45247,
            "terminalName": "TP860621066646235",
            "terminalMac": "TP860621066646235",
            "terminalStatus": "TACHOGRAPH_NOT_AVAILABLE_WITH_CARD_DOWNLOAD",
            "driver1": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 2327,
                "drivingLast2WeeksHR": "38h:47m",
                "drivingTwoWeeklyRemaining": 3073,
                "drivingTwoWeeklyRemainingHR": "51h:13m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "BREAK/REST",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": 1780287537,
                    "uploadTimeStampHR": "2026.06.01 04:18:57"
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 3600,
                    "workTimeWeeklyMaxHR": "60h:00m",
                    "workTimeAverageLast16Weeks": 2161,
                    "workTimeAverageLast16WeeksHR": "36h:01m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 600,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 1780286760,
                    "workInformationUpdateTimeHR": "2026.06.01 04:06:00"
                },
                "projections": {
                    "nextWorkStart": 1780382182,
                    "nextWorkStartHR": "2026.06.02 06:36:22",
                    "nextWorkLength": 600
                },
                "driverName": "Janos-Attila Pazsint",
                "identification": "000000000EDTT0",
                "driverId": 55910,
                "nextWeeklyBreakStart": 1780805160,
                "nextWeeklyBreakStartHR": "2026.06.07 04:06:00",
                "nextDailyBreakStart": 1780373160,
                "nextDailyBreakStartHR": "2026.06.02 04:06:00",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 1780287537,
                "baseTimeHR": "2026.06.01 04:18:57"
            },
            "driver2": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 0,
                "drivingLast2WeeksHR": "0h:00m",
                "drivingTwoWeeklyRemaining": 5400,
                "drivingTwoWeeklyRemainingHR": "90h:00m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "NOT_AVAILABLE",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": null,
                    "uploadTimeStampHR": ""
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 0,
                    "workTimeWeeklyMaxHR": "0h:00m",
                    "workTimeAverageLast16Weeks": 0,
                    "workTimeAverageLast16WeeksHR": "0h:00m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 0,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 0,
                    "workInformationUpdateTimeHR": "1970.01.01 00:00:00"
                },
                "projections": {
                    "nextWorkStart": 0,
                    "nextWorkStartHR": "1970.01.01 00:00:00",
                    "nextWorkLength": 0
                },
                "driverName": "",
                "identification": "",
                "driverId": 0,
                "nextWeeklyBreakStart": 0,
                "nextWeeklyBreakStartHR": "",
                "nextDailyBreakStart": 0,
                "nextDailyBreakStartHR": "",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 0,
                "baseTimeHR": ""
            },
            "vehicleRegistrationNumber": "MS-58-KRI",
            "vehicleIdentificationNumber": "YV2RT40A0RB459759",
            "outOfScopeMode": null,
            "vehicleSpeed": null,
            "updatedAt": null,
            "uploadInformation": null
        },
        {
            "terminalId": 45251,
            "terminalName": "TP860621066639743",
            "terminalMac": "TP860621066639743",
            "terminalStatus": "TACHOGRAPH_NOT_AVAILABLE_WITH_CARD_DOWNLOAD",
            "driver1": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 0,
                "drivingLast2WeeksHR": "0h:00m",
                "drivingTwoWeeklyRemaining": 5400,
                "drivingTwoWeeklyRemainingHR": "90h:00m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "NOT_AVAILABLE",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": 1779718986,
                    "uploadTimeStampHR": "2026.05.25 14:23:06"
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 0,
                    "workTimeWeeklyMaxHR": "0h:00m",
                    "workTimeAverageLast16Weeks": 0,
                    "workTimeAverageLast16WeeksHR": "0h:00m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 0,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 0,
                    "workInformationUpdateTimeHR": "1970.01.01 00:00:00"
                },
                "projections": {
                    "nextWorkStart": 0,
                    "nextWorkStartHR": "1970.01.01 00:00:00",
                    "nextWorkLength": 0
                },
                "driverName": "Szilard Tibor Bereczki",
                "identification": "000000000ANOP0",
                "driverId": 118785,
                "nextWeeklyBreakStart": 0,
                "nextWeeklyBreakStartHR": "",
                "nextDailyBreakStart": 0,
                "nextDailyBreakStartHR": "",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 1779718986,
                "baseTimeHR": "2026.05.25 14:23:06"
            },
            "driver2": {
                "drivingContinuousMax": 270,
                "drivingContinuousMaxHR": "4h:30m",
                "drivingContinuousRemaining": 270,
                "drivingContinuousRemainingHR": "4h:30m",
                "drivingContinuousTime": 0,
                "drivingContinuousTimeHR": "0h:00m",
                "drivingDailyRemaining": 600,
                "drivingDailyRemainingHR": "10h:00m",
                "drivingDailyMax": 600,
                "drivingDailyMaxHR": "10h:00m",
                "drivingDailyTime": 0,
                "drivingDailyTimeHR": "0h:00m",
                "drivingWeeklyMax": 3360,
                "drivingWeeklyMaxHR": "56h:00m",
                "drivingWeeklyTime": 0,
                "drivingWeeklyTimeHR": "0h:00m",
                "drivingWeeklyRemaining": 3360,
                "drivingWeeklyRemainingHR": "56h:00m",
                "drivingLast2Weeks": 0,
                "drivingLast2WeeksHR": "0h:00m",
                "drivingTwoWeeklyRemaining": 5400,
                "drivingTwoWeeklyRemainingHR": "90h:00m",
                "drivingTwoWeeklyMax": 5400,
                "drivingTwoWeeklyMaxHR": "90h:00m",
                "moreThan9hDrivingTimesFromLastWeeklyBreak": 0,
                "moreThan9hDrivingTimesFromLastWeeklyBreakMax": 2,
                "lessThan11hBreakTimesFromLastWeeklyBreak": 0,
                "lessThan11hBreakTimesFromLastWeeklyBreakMax": 3,
                "lastActivity": {
                    "activity": "NOT_AVAILABLE",
                    "durationOfLastActivity": 0
                },
                "uploadInformation": {
                    "uploadTimeStamp": null,
                    "uploadTimeStampHR": ""
                },
                "workTimes": {
                    "workTimeWeekly": 0,
                    "workTimeWeeklyHR": "0h:00m",
                    "workTimeWeeklyMax": 0,
                    "workTimeWeeklyMaxHR": "0h:00m",
                    "workTimeAverageLast16Weeks": 0,
                    "workTimeAverageLast16WeeksHR": "0h:00m",
                    "nightWorkPerformedThisDay": false,
                    "maxWorkWhenNightWorkPerformed": 0,
                    "workContinuousWithoutBreak": 0,
                    "workContinuousWithoutBreakMax": null,
                    "totalWorkToday": 0,
                    "justWorkForToday": 0,
                    "workInformationUpdateTime": 0,
                    "workInformationUpdateTimeHR": "1970.01.01 00:00:00"
                },
                "projections": {
                    "nextWorkStart": 0,
                    "nextWorkStartHR": "1970.01.01 00:00:00",
                    "nextWorkLength": 0
                },
                "driverName": "",
                "identification": "",
                "driverId": 0,
                "nextWeeklyBreakStart": 0,
                "nextWeeklyBreakStartHR": "",
                "nextDailyBreakStart": 0,
                "nextDailyBreakStartHR": "",
                "lastActivityDay": {
                    "crew": false
                },
                "lastDailyRest": {
                    "start": 0,
                    "restStartHR": "",
                    "end": 0,
                    "restEndHR": ""
                },
                "lastWeeklyRest": {
                    "start": 0,
                    "startHR": "",
                    "end": 0,
                    "endHR": ""
                },
                "baseTime": 0,
                "baseTimeHR": ""
            },
            "vehicleRegistrationNumber": "B-355-KRI",
            "vehicleIdentificationNumber": "YV2RT40A4NB383618",
            "outOfScopeMode": null,
            "vehicleSpeed": null,
            "updatedAt": null,
            "uploadInformation": null
        },
        {
            "terminalId": 45252,
            "terminalName": "TP860621066639354",
            "terminalMac": "TP860621066639354",
            "terminalStatus": "TACHOGRAPH_AVAILABLE_CARD_INSERTED_WITHOUT_CARD_DOWNLOAD",
            "driver1": null,
            "driver2": null,
            "vehicleRegistrationNumber": "MS-77-KRI",
            "vehicleIdentificationNumber": "YV2RT40A0NB393515",
            "outOfScopeMode": null,
            "vehicleSpeed": null,
            "updatedAt": null,
            "uploadInformation": null
        }
    ],
    "totalCount": 6
};


type Driver = {
    drivingContinuousMax: number;
    drivingContinuousMaxHR: string | null;
    drivingContinuousTime: number;
    drivingContinuousTimeHR: string | null;
    drivingContinuousRemaining: number;
    drivingContinuousRemainingHR: string | null;
    drivingDailyMax: number;
    drivingDailyMaxHR: string | null;
    drivingDailyTime: number;
    drivingDailyTimeHR: string | null;
    /** true when the terminal cannot calculate the daily driving time from the card data */
    drivingDailyTimeNotAvailable?: boolean | null;
    drivingDailyRemaining: number;
    drivingDailyRemainingHR: string | null;
    drivingWeeklyMax: number;
    drivingWeeklyMaxHR: string | null;
    drivingWeeklyTime: number;
    drivingWeeklyTimeHR: string | null;
    drivingWeeklyRemaining: number;
    drivingWeeklyRemainingHR: string | null;
    drivingTwoWeeklyMax: number;
    drivingTwoWeeklyMaxHR: string | null;
    drivingLast2Weeks: number;
    drivingLast2WeeksHR: string | null;
    drivingTwoWeeklyRemaining: number;
    drivingTwoWeeklyRemainingHR: string | null;
    moreThan9hDrivingTimesFromLastWeeklyBreak: number;
    moreThan9hDrivingTimesFromLastWeeklyBreakMax: number;
    lessThan11hBreakTimesFromLastWeeklyBreak: number;
    lessThan11hBreakTimesFromLastWeeklyBreakMax: number;
    driverName: string | null;
    identification: string | null;
    driverId: number | null;
    nextWeeklyBreakStart: number;
    nextWeeklyBreakStartHR: string | null;
    // newer API versions only
    nextWeeklyBreakLength?: number;
    nextBreak?: number;
    nextBreakField?: string | null;
    nextDailyBreakStart: number;
    nextDailyBreakStartHR: string | null;
    baseTime: number;
    baseTimeHR: string | null;
    lastActivity: {
        activity: string | null;
        durationOfLastActivity: number;
    };
    uploadInformation: {
        uploadTimeStamp: number | null;
        uploadTimeStampHR: string | null;
    };
    workTimes: {
        workTimeWeekly: number;
        workTimeWeeklyHR: string | null;
        workTimeWeeklyMax: number;
        workTimeWeeklyMaxHR: string | null;
        workTimeAverageLast16Weeks: number;
        workTimeAverageLast16WeeksHR: string | null;
        nightWorkPerformedThisDay: boolean;
        maxWorkWhenNightWorkPerformed: number;
        workContinuousWithoutBreak: number;
        workContinuousWithoutBreakMax: string | null;
        totalWorkToday: number;
        justWorkForToday: number;
        workInformationUpdateTime: number;
        workInformationUpdateTimeHR: string | null;
    };
    projections: {
        nextWorkStart: number;
        nextWorkStartHR: string | null;
        nextWorkLength: number;
    };
    lastActivityDay: {
        crew: boolean;
    };
    lastDailyRest: {
        start: number;
        restStartHR: string | null;
        end: number;
        restEndHR: string | null;
    };
    lastWeeklyRest: {
        start: number;
        startHR: string | null;
        end: number;
        endHR: string | null;
    };
    cardInserted?: boolean | null;
};

type TachographParameter = {
    terminalId: number;
    terminalName: string | null;
    terminalMac: string | null;
    /** legacy field, no longer sent by readTachographParameters; superseded by ta/ci/cd */
    terminalStatus?: string | null;
    /** TA - tachograph available */
    ta?: boolean | null;
    /** CI - card inserted */
    ci?: boolean | null;
    /** CD - card download */
    cd?: boolean | null;
    driver1: Driver | null;
    driver2: Driver | null;
    vehicleRegistrationNumber: string | null;
    vehicleIdentificationNumber: string | null;
    outOfScopeMode: boolean | null;
    vehicleSpeed: number | null;
    updatedAt: string | null;
    uploadInformation: {
        uploadTimeStamp: number | null;
        uploadTimeStampHR: string | null;
    } | null;
};

type DeepNullable<T> = {
    [K in keyof T]: T[K] extends Array<infer U>
    ? Array<DeepNullable<U> | null> | null
    : T[K] extends object
    ? DeepNullable<T[K]> | null
    : T[K] | null;
};

type TachographResponse = {
    tachographParameters: Array<DeepNullable<TachographParameter> | null> | null;
    totalCount: number | null;
};